import { useEffect, useRef, useState } from "react";
import ChannelConnection from "../../js/connection/ChannelConnection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faMicrophone, faMicrophoneSlash,
    faVideo, faVideoSlash,
    faDesktop, faStopCircle,
    faCommentDots, faUsers,
    faCog, faHandPaper,
    faExpand, faCompress,
    faPhoneSlash, faEllipsisH,
    faSmile, faCircle,
    faLink,
    faVolumeMute, faVolumeUp,
    faTv, faUserSlash,
    faClosedCaptioning,
} from "@fortawesome/free-solid-svg-icons";
import { useParams } from "react-router";
import ChannelSettingsModal from "./ChannelSettingsModal";
import {
    collectPipTiles,
    createPipCanvas,
    paintPipFrame,
} from "../../js/video/PictureInPicture";

export type SidePanel = 'chat' | 'participants' | null;

type Props = {
    connection: ChannelConnection;
    isMobileView: boolean;
    sidePanel: SidePanel;
    setPanel: (p: SidePanel) => void;
    captionsEnabled: boolean;
    onToggleCaptions: () => void;
    meetingStartTime: Date;
    onMuteAll: () => void;
    unreadMessages: number;
    showEmojiPicker: boolean;
    onToggleEmojiPicker: () => void;
};

export default function ChannelToolbar({
    connection,
    isMobileView,
    sidePanel,
    setPanel,
    captionsEnabled,
    onToggleCaptions,
    meetingStartTime,
    onMuteAll,
    unreadMessages,
    showEmojiPicker,
    onToggleEmojiPicker,
}: Props) {
    const [, forceRender] = useState({});
    const [showSettingsModal, setSettingsModal] = useState(false);
    const [isHandRaised, setHandRaised] = useState(false);
    const [isFullscreen, setFullscreen] = useState(false);
    const [isScreensharingActive, setScreensharingActive] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isPiP, setIsPiP] = useState(false);
    const [isNoiseSuppressed, setNoiseSuppressed] = useState(true);
    const [showMoreMenu, setShowMoreMenu] = useState(false);
    const [elapsed, setElapsed] = useState('00:00');
    const recorderRef = useRef<MediaRecorder | null>(null);
    const recChunks = useRef<Blob[]>([]);
    const moreMenuRef = useRef<HTMLDivElement>(null);
    const channelId = useRef(useParams().id);
    const pipCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const pipVideoRef = useRef<HTMLVideoElement | null>(null);
    const pipRafRef = useRef<number | null>(null);

    // Meeting timer
    useEffect(() => {
        const iv = setInterval(() => {
            const diff = Math.floor((Date.now() - meetingStartTime.getTime()) / 1000);
            const h = Math.floor(diff / 3600);
            const m = Math.floor((diff % 3600) / 60);
            const s = diff % 60;
            const hPart = h > 0 ? `${h.toString().padStart(2, '0')}:` : '';
            setElapsed(`${hPart}${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`);
        }, 1000);
        return () => clearInterval(iv);
    }, [meetingStartTime]);

    // Screenshare state
    useEffect(() => {
        const cb = (data: any) => setScreensharingActive(!!data && data.isLocal);
        connection.events.addListener("onscreenshareupdate", cb);
        return () => connection.events.removeListener("onscreenshareupdate", cb);
    }, [connection]);

    // PiP browser events + cleanup on unmount
    useEffect(() => {
        const enter = () => setIsPiP(true);
        const leave = () => { setIsPiP(false); stopPiPCanvas(); };
        document.addEventListener('enterpictureinpicture', enter);
        document.addEventListener('leavepictureinpicture', leave);
        return () => {
            document.removeEventListener('enterpictureinpicture', enter);
            document.removeEventListener('leavepictureinpicture', leave);
            stopPiPCanvas();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Close more menu on outside click
    useEffect(() => {
        if (!showMoreMenu) return;
        function onDown(e: MouseEvent) {
            if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
                setShowMoreMenu(false);
            }
        }
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [showMoreMenu]);

    // ── Actions ──────────────────────────────────────────────────

    function toggleVideo() { connection.toggleLocalVideo(); forceRender({}); }
    function toggleAudio() { connection.toggleLocalAudio(); forceRender({}); }
    function toggleScreenshare() { connection.screenshare.toggleScreenshare(); forceRender({}); }
    function leaveCall() {
        const el = document.getElementById("confirmLeave");
        if (el) el.classList.toggle("is-active");
    }

    function toggleFullscreen() {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().then(() => setFullscreen(true)).catch(() => {});
        } else {
            document.exitFullscreen().then(() => setFullscreen(false)).catch(() => {});
        }
    }

    function toggleRaiseHand() {
        const next = !isHandRaised;
        setHandRaised(next);
        if (next) {
            try {
                connection.messager.sendChatMessage(
                    JSON.stringify({ type: 'raise_hand', alias: connection.channel.getUserAlias() })
                );
            } catch {}
        }
    }

    async function toggleRecording() {
        if (isRecording) {
            recorderRef.current?.stop();
            recorderRef.current = null;
            setIsRecording(false);
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
            const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
                ? 'video/webm;codecs=vp9' : 'video/webm';
            const recorder = new MediaRecorder(stream, { mimeType });
            recChunks.current = [];
            recorder.ondataavailable = e => { if (e.data.size > 0) recChunks.current.push(e.data); };
            recorder.onstop = () => {
                const blob = new Blob(recChunks.current, { type: 'video/webm' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `holoxr-${new Date().toISOString().slice(0, 10)}.webm`;
                a.click();
                URL.revokeObjectURL(url);
                stream.getTracks().forEach(t => t.stop());
            };
            stream.getVideoTracks()[0]?.addEventListener('ended', () => {
                setIsRecording(false);
                recorderRef.current = null;
            });
            recorder.start(1000);
            recorderRef.current = recorder;
            setIsRecording(true);
        } catch {}
        setShowMoreMenu(false);
    }

    async function togglePiP() {
        try {
            if (document.pictureInPictureElement) {
                await document.exitPictureInPicture();
                stopPiPCanvas();
            } else {
                await startPiP();
            }
        } catch {}
        setShowMoreMenu(false);
    }

    async function startPiP() {
        const container = document.getElementById('video');
        if (!container) return;

        if (!pipCanvasRef.current) {
            pipCanvasRef.current = createPipCanvas();
        }
        const canvas = pipCanvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        function drawFrame() {
            const tiles = collectPipTiles(container!);
            paintPipFrame(ctx!, canvas, tiles);
            pipRafRef.current = requestAnimationFrame(drawFrame);
        }
        drawFrame();

        if (!pipVideoRef.current) {
            pipVideoRef.current = document.createElement('video');
            pipVideoRef.current.muted = true;
            pipVideoRef.current.playsInline = true;
            pipVideoRef.current.style.cssText =
                'position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;top:-2px;left:-2px;';
            document.body.appendChild(pipVideoRef.current);
        }
        const pipVid = pipVideoRef.current;
        pipVid.srcObject = canvas.captureStream(30);

        pipVid.addEventListener('leavepictureinpicture', stopPiPCanvas, { once: true });

        await pipVid.play();
        await pipVid.requestPictureInPicture();
    }

    function stopPiPCanvas() {
        if (pipRafRef.current !== null) {
            cancelAnimationFrame(pipRafRef.current);
            pipRafRef.current = null;
        }
        if (pipVideoRef.current) {
            const s = pipVideoRef.current.srcObject as MediaStream | null;
            s?.getTracks().forEach(t => t.stop());
            pipVideoRef.current.srcObject = null;
            pipVideoRef.current.remove();
            pipVideoRef.current = null;
        }
    }

    function copyInviteLink() {
        navigator.clipboard.writeText(window.location.href).catch(() => {});
        setShowMoreMenu(false);
    }

    function handleMuteAll() {
        onMuteAll();
        setShowMoreMenu(false);
    }

    const isVideoOn = connection.isLocalVideoEnabled;
    const isAudioOn = connection.isLocalAudioEnabled;

    return (
        <div className="gm-toolbar-wrap">
            <div className="gm-toolbar">
                {/* ── Left: timer + session ID ── */}
                <div className="gm-toolbar-left">
                    <span className="gm-timer">{elapsed}</span>
                    {!isMobileView && (
                        <span className="gm-session-id">{channelId.current}</span>
                    )}
                </div>

                {/* ── Center: all controls ── */}
                <div className="gm-toolbar-center">
                    {/* Primary controls — always visible */}
                    <TBtn
                        icon={isAudioOn ? faMicrophone : faMicrophoneSlash}
                        label={isAudioOn ? 'Mute' : 'Unmute'}
                        active={isAudioOn}
                        danger={!isAudioOn}
                        onClick={toggleAudio}
                    />
                    <TBtn
                        icon={isVideoOn ? faVideo : faVideoSlash}
                        label={isVideoOn ? 'Stop video' : 'Start video'}
                        active={isVideoOn}
                        danger={!isVideoOn}
                        onClick={toggleVideo}
                    />

                    {/* Desktop/tablet extras */}
                    {!isMobileView && (
                        <>
                            <TBtn
                                icon={isScreensharingActive ? faStopCircle : faDesktop}
                                label={isScreensharingActive ? 'Stop' : 'Present'}
                                active={isScreensharingActive}
                                onClick={toggleScreenshare}
                            />
                            <TBtn
                                icon={faHandPaper}
                                label={isHandRaised ? 'Lower hand' : 'Raise hand'}
                                active={isHandRaised}
                                hand={isHandRaised}
                                onClick={toggleRaiseHand}
                            />
                            <TBtn
                                icon={faSmile}
                                label="React"
                                active={showEmojiPicker}
                                onClick={onToggleEmojiPicker}
                            />
                            <div className="gm-divider" />
                        </>
                    )}

                    <TBtn
                        icon={faCommentDots}
                        label="Chat"
                        active={sidePanel === 'chat'}
                        badge={sidePanel !== 'chat' ? unreadMessages : 0}
                        onClick={() => setPanel(sidePanel === 'chat' ? null : 'chat')}
                    />
                    <TBtn
                        icon={faUsers}
                        label="People"
                        active={sidePanel === 'participants'}
                        onClick={() => setPanel(sidePanel === 'participants' ? null : 'participants')}
                    />

                    {!isMobileView && (
                        <TBtn
                            icon={faClosedCaptioning}
                            label={captionsEnabled ? 'CC on' : 'Captions'}
                            active={captionsEnabled}
                            onClick={onToggleCaptions}
                        />
                    )}

                    {/* More menu */}
                    <div className="gm-more-wrap" ref={moreMenuRef}>
                        <TBtn
                            icon={faEllipsisH}
                            label="More"
                            active={showMoreMenu}
                            onClick={() => setShowMoreMenu(p => !p)}
                        />
                        {showMoreMenu && (
                            <div className="gm-more-menu">
                                {isMobileView && (
                                    <>
                                        <button className="gm-more-item" onClick={() => { toggleScreenshare(); setShowMoreMenu(false); }}>
                                            <span className="gm-more-icon">
                                                <FontAwesomeIcon icon={isScreensharingActive ? faStopCircle : faDesktop} />
                                            </span>
                                            <span>{isScreensharingActive ? 'Stop presenting' : 'Present screen'}</span>
                                        </button>
                                        <button className="gm-more-item" onClick={() => { toggleRaiseHand(); setShowMoreMenu(false); }}>
                                            <span className={`gm-more-icon ${isHandRaised ? 'recording' : ''}`}>
                                                <FontAwesomeIcon icon={faHandPaper} />
                                            </span>
                                            <span>{isHandRaised ? 'Lower hand' : 'Raise hand'}</span>
                                        </button>
                                        <button className="gm-more-item" onClick={() => { onToggleEmojiPicker(); setShowMoreMenu(false); }}>
                                            <span className="gm-more-icon">
                                                <FontAwesomeIcon icon={faSmile} />
                                            </span>
                                            <span>React</span>
                                        </button>
                                        <button className="gm-more-item" onClick={() => { onToggleCaptions(); setShowMoreMenu(false); }}>
                                            <span className="gm-more-icon">
                                                <FontAwesomeIcon icon={faClosedCaptioning} />
                                            </span>
                                            <span>{captionsEnabled ? 'Turn captions off' : 'Turn captions on'}</span>
                                        </button>
                                        <div className="gm-more-divider" />
                                    </>
                                )}
                                <button className="gm-more-item" onClick={toggleRecording}>
                                    <span className={`gm-more-icon ${isRecording ? 'recording' : ''}`}>
                                        <FontAwesomeIcon icon={isRecording ? faStopCircle : faCircle} />
                                    </span>
                                    <span>{isRecording ? 'Stop recording' : 'Record meeting'}</span>
                                    {isRecording && <span className="gm-rec-dot" />}
                                </button>
                                <button className="gm-more-item" onClick={togglePiP}>
                                    <span className="gm-more-icon">
                                        <FontAwesomeIcon icon={faTv} />
                                    </span>
                                    <span>{isPiP ? 'Exit picture-in-picture' : 'Picture in picture'}</span>
                                </button>
                                <button className="gm-more-item" onClick={copyInviteLink}>
                                    <span className="gm-more-icon">
                                        <FontAwesomeIcon icon={faLink} />
                                    </span>
                                    <span>Copy invite link</span>
                                </button>
                                <div className="gm-more-divider" />
                                <button className="gm-more-item" onClick={handleMuteAll}>
                                    <span className="gm-more-icon">
                                        <FontAwesomeIcon icon={faUserSlash} />
                                    </span>
                                    <span>Ask everyone to mute</span>
                                </button>
                                <button
                                    className="gm-more-item"
                                    onClick={() => { setNoiseSuppressed(p => !p); setShowMoreMenu(false); }}
                                >
                                    <span className="gm-more-icon">
                                        <FontAwesomeIcon icon={isNoiseSuppressed ? faVolumeUp : faVolumeMute} />
                                    </span>
                                    <span>Noise suppression: {isNoiseSuppressed ? 'On' : 'Off'}</span>
                                    <span className={`gm-toggle-pill ${isNoiseSuppressed ? 'on' : ''}`} />
                                </button>
                                <div className="gm-more-divider" />
                                <button className="gm-more-item" onClick={() => { setSettingsModal(true); setShowMoreMenu(false); }}>
                                    <span className="gm-more-icon">
                                        <FontAwesomeIcon icon={faCog} />
                                    </span>
                                    <span>Settings</span>
                                </button>
                                <button className="gm-more-item" onClick={() => { toggleFullscreen(); setShowMoreMenu(false); }}>
                                    <span className="gm-more-icon">
                                        <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
                                    </span>
                                    <span>{isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Right: recording indicator + leave ── */}
                <div className="gm-toolbar-right">
                    {isRecording && (
                        <div className="gm-rec-indicator">
                            <span className="gm-rec-dot-anim" />
                            <span>REC</span>
                        </div>
                    )}
                    <button className="gm-end-btn" onClick={leaveCall} title="Leave call">
                        <FontAwesomeIcon icon={faPhoneSlash} />
                        {!isMobileView && <span>Leave</span>}
                    </button>
                </div>
            </div>

            {showSettingsModal && (
                <ChannelSettingsModal
                    show={showSettingsModal}
                    localMedia={connection.getLocalMedia()}
                    onClose={() => setSettingsModal(false)}
                />
            )}

            <style>{`
                .gm-toolbar-wrap {
                    position: absolute;
                    left: 0;
                    right: 0;
                    bottom: 18px;
                    z-index: 45;
                    display: flex;
                    justify-content: center;
                    padding: 0 1rem;
                    pointer-events: none;
                    -webkit-transform: translateZ(0);
                    transform: translateZ(0);
                }
                .gm-toolbar {
                    pointer-events: auto;
                    display: flex;
                    align-items: center;
                    gap: 0.55rem;
                    max-width: calc(100vw - 2rem);
                    min-height: 64px;
                    padding: 0.45rem 0.55rem 0.45rem 0.85rem;
                    background: var(--hx-card);
                    border: 1px solid var(--hx-line);
                    border-radius: 22px;
                    box-shadow: var(--hx-shadow);
                }
                .gm-toolbar-left {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding-right: 0.35rem;
                    flex-shrink: 0;
                }
                .gm-timer {
                    font-size: 0.78rem;
                    font-weight: 650;
                    color: var(--hx-ink-2);
                    font-family: 'SF Mono', 'Fira Code', ui-monospace, monospace;
                    letter-spacing: 0.04em;
                    padding: 0.35rem 0.55rem;
                    border-radius: 999px;
                    background: var(--hx-line-2);
                    border: 1px solid var(--hx-line);
                }
                .gm-session-id { display: none; }
                .gm-toolbar-center {
                    display: flex;
                    align-items: center;
                    gap: 0.15rem;
                    flex-wrap: nowrap;
                    min-width: 0;
                }
                .gm-toolbar-right {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 0.55rem;
                    padding-left: 0.2rem;
                    flex-shrink: 0;
                }
                .gm-divider {
                    width: 1px;
                    height: 28px;
                    background: var(--hx-line);
                    margin: 0 0.3rem;
                    flex-shrink: 0;
                }

                .gm-btn {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.15rem;
                    padding: 0.3rem 0.45rem;
                    border: none;
                    border-radius: 12px;
                    cursor: pointer;
                    background: transparent;
                    color: var(--hx-muted);
                    transition: color 0.15s ease, background 0.15s ease;
                    min-width: 48px;
                    position: relative;
                    font-family: inherit;
                }
                .gm-btn:hover { background: var(--hx-line-2); color: var(--hx-ink); }
                .gm-btn.active { color: var(--hx-blue); }
                .gm-btn.danger { color: var(--hx-danger); }
                .gm-btn.raised { color: #d97706; }
                .gm-btn-icon {
                    width: 40px; height: 40px;
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1rem;
                    background: var(--hx-line-2);
                    transition: background 0.15s ease, box-shadow 0.15s ease;
                    position: relative;
                }
                .gm-btn:hover .gm-btn-icon { background: var(--hx-line); }
                .gm-btn.active .gm-btn-icon {
                    background: var(--hx-blue-soft);
                    color: var(--hx-blue);
                    box-shadow: inset 0 0 0 1px var(--hx-blue-border);
                }
                .gm-btn.danger .gm-btn-icon { background: #fef2f2; box-shadow: inset 0 0 0 1px #fecaca; }
                .gm-btn.raised .gm-btn-icon {
                    background: #fffbeb;
                    color: #d97706;
                    box-shadow: inset 0 0 0 1px #fde68a;
                }
                .gm-btn-label {
                    font-size: 0.62rem;
                    font-weight: 550;
                    color: var(--hx-muted);
                    white-space: nowrap;
                    letter-spacing: 0.01em;
                }
                .gm-btn.active .gm-btn-label { color: var(--hx-blue); }
                .gm-badge {
                    position: absolute;
                    top: -3px; right: -3px;
                    background: var(--hx-blue);
                    color: white;
                    border-radius: 50%;
                    width: 17px; height: 17px;
                    font-size: 0.58rem; font-weight: 700;
                    display: flex; align-items: center; justify-content: center;
                    border: 2px solid var(--hx-card);
                }

                .gm-more-wrap { position: relative; }
                .gm-more-menu {
                    position: absolute;
                    bottom: calc(100% + 14px);
                    right: 0;
                    background: var(--hx-card);
                    border: 1px solid var(--hx-line);
                    border-radius: 14px;
                    padding: 0.4rem;
                    min-width: 240px;
                    box-shadow: var(--hx-shadow);
                    z-index: 1000;
                    animation: moreMenuIn 0.15s ease;
                }
                @keyframes moreMenuIn {
                    from { opacity: 0; transform: translateY(6px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .gm-more-item {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    width: 100%;
                    padding: 0.65rem 0.85rem;
                    border: none;
                    border-radius: 10px;
                    background: transparent;
                    color: var(--hx-ink-2);
                    font-size: 0.86rem;
                    cursor: pointer;
                    text-align: left;
                    font-family: inherit;
                    transition: background 0.12s;
                }
                .gm-more-item:hover { background: var(--hx-line-2); }
                .gm-more-icon {
                    width: 22px;
                    display: flex; align-items: center; justify-content: center;
                    color: var(--hx-muted); font-size: 0.9rem; flex-shrink: 0;
                }
                .gm-more-icon.recording { color: var(--hx-danger); }
                .gm-more-divider {
                    height: 1px;
                    background: var(--hx-line);
                    margin: 0.3rem 0.4rem;
                }
                .gm-toggle-pill {
                    margin-left: auto;
                    width: 30px; height: 16px;
                    border-radius: 20px;
                    background: var(--hx-line);
                    position: relative;
                    flex-shrink: 0;
                    transition: background 0.2s;
                }
                .gm-toggle-pill.on { background: var(--hx-blue); }
                .gm-toggle-pill::after {
                    content: '';
                    position: absolute;
                    top: 2px; left: 2px;
                    width: 12px; height: 12px;
                    border-radius: 50%;
                    background: white;
                    transition: transform 0.2s;
                }
                .gm-toggle-pill.on::after { transform: translateX(14px); }

                .gm-rec-indicator {
                    display: flex;
                    align-items: center;
                    gap: 0.35rem;
                    font-size: 0.7rem;
                    font-weight: 700;
                    color: var(--hx-danger-hover);
                    letter-spacing: 0.08em;
                    padding: 0.3rem 0.55rem;
                    border-radius: 999px;
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                }
                .gm-rec-dot-anim {
                    width: 7px; height: 7px;
                    border-radius: 50%;
                    background: var(--hx-danger);
                    animation: recPulse 1.2s ease-in-out infinite;
                }
                @keyframes recPulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50%       { opacity: 0.4; transform: scale(0.85); }
                }
                .gm-rec-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--hx-danger); }

                .gm-end-btn {
                    background: linear-gradient(180deg, var(--hx-danger) 0%, var(--hx-danger-hover) 100%);
                    border: none;
                    border-radius: 999px;
                    padding: 0.65rem 1.15rem;
                    color: #fff;
                    font-size: 0.84rem;
                    font-weight: 650;
                    cursor: pointer;
                    display: flex; align-items: center; gap: 0.45rem;
                    transition: filter 0.15s, transform 0.1s;
                    letter-spacing: 0.01em;
                    box-shadow: 0 4px 14px rgba(220, 38, 38, 0.28);
                    font-family: inherit;
                }
                .gm-end-btn:hover { filter: brightness(1.08); transform: translateY(-1px); }
                .gm-end-btn:active { transform: translateY(0); }

                @media (max-width: 900px) {
                    .gm-btn-label { display: none; }
                    .gm-btn { min-width: 42px; padding: 0.25rem; }
                    .gm-toolbar { padding: 0.4rem 0.45rem 0.4rem 0.65rem; min-height: 58px; }
                }

                @media (max-width: 768px) {
                    .gm-toolbar-wrap {
                        bottom: calc(10px + env(safe-area-inset-bottom, 0px));
                        padding: 0 max(0.5rem, env(safe-area-inset-right, 0px)) 0 max(0.5rem, env(safe-area-inset-left, 0px));
                    }
                    .gm-toolbar {
                        width: 100%;
                        max-width: 100%;
                        justify-content: space-between;
                        gap: 0.2rem;
                        padding: 0.35rem 0.4rem;
                        min-height: 54px;
                        border-radius: 18px;
                    }
                    .gm-toolbar-left { display: none; }
                    .gm-toolbar-center {
                        flex: 1;
                        justify-content: center;
                        gap: 0.1rem;
                        min-width: 0;
                    }
                    .gm-toolbar-right { flex: 0; padding-left: 0; }
                    .gm-btn { min-width: 40px; padding: 0.2rem; }
                    .gm-btn-icon { width: 38px; height: 38px; font-size: 0.95rem; }
                    .gm-timer { display: none; }
                    .gm-end-btn {
                        padding: 0;
                        border-radius: 50%;
                        width: 40px; height: 40px;
                        justify-content: center;
                        box-shadow: none;
                    }
                    .gm-divider { display: none; }
                    .gm-more-menu {
                        right: auto;
                        left: 50%;
                        transform: translateX(-50%);
                        min-width: min(280px, calc(100vw - 1.5rem));
                        max-height: min(70vh, 420px);
                        overflow-y: auto;
                    }
                    .gm-rec-indicator { display: none; }
                }

                @media (max-width: 380px) {
                    .gm-btn { min-width: 36px; }
                    .gm-btn-icon { width: 34px; height: 34px; font-size: 0.88rem; }
                    .gm-end-btn { width: 36px; height: 36px; }
                }
            `}</style>
        </div>
    );
}

function TBtn({
    icon, label, active, danger, hand, badge, onClick,
}: {
    icon: any; label: string; active?: boolean; danger?: boolean;
    hand?: boolean; badge?: number; onClick: () => void;
}) {
    let cls = 'gm-btn';
    if (active) cls += ' active';
    if (danger && !active) cls += ' danger';
    if (hand) cls += ' raised';

    return (
        <button className={cls} onClick={onClick} title={label}>
            <div className="gm-btn-icon">
                <FontAwesomeIcon icon={icon} />
                {!!badge && <span className="gm-badge">{badge > 9 ? '9+' : badge}</span>}
            </div>
            <span className="gm-btn-label">{label}</span>
        </button>
    );
}
